import sanitizeHTML from '../../../services/sanitizeHTML';

const HEADING_MAX_LEN = 80;
const TERMINATOR = /[.?!:,]$/;

/**
 * Pull a leading title out of a pasted HTML fragment for a heading-bearing block:
 * a first `<h1>`–`<h4>`, or a first `<p>` that is entirely bold and reads like a
 * title (short, no sentence terminator). Returns the remaining HTML with that
 * element removed. When nothing qualifies, `heading` is undefined and `restHtml`
 * is the input unchanged.
 */
export function extractLeadingHeadingFromHtml(html: string): {
  heading?: string;
  restHtml: string;
} {
  if (typeof document === 'undefined' || html.trim() === '') return { restHtml: html };

  const div = document.createElement('div');
  div.innerHTML = sanitizeHTML(html);

  const first = firstElement(div);
  if (!first) return { restHtml: html };

  const text = (first.textContent ?? '').trim();
  const tag = first.tagName.toLowerCase();
  const isHeadingTag = /^h[1-4]$/.test(tag);
  const isBoldTitleParagraph =
    tag === 'p' &&
    text.length > 0 &&
    text.length <= HEADING_MAX_LEN &&
    !TERMINATOR.test(text) &&
    isEntirelyBold(first);

  if (!isHeadingTag && !isBoldTitleParagraph) return { restHtml: html };

  first.remove();
  return { heading: text, restHtml: div.innerHTML };
}

/** The first child element, unless loose text precedes it (then there is no clean heading). */
function firstElement(root: HTMLElement): HTMLElement | null {
  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType === Node.ELEMENT_NODE) return node as HTMLElement;
    if (node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim() !== '') return null;
  }
  return null;
}

/** True when removing every `<b>`/`<strong>` leaves no visible text — the line is all bold. */
function isEntirelyBold(el: HTMLElement): boolean {
  if (!el.querySelector('b, strong')) return false;
  const clone = el.cloneNode(true) as HTMLElement;
  clone.querySelectorAll('b, strong').forEach((node) => node.remove());
  return (clone.textContent ?? '').trim() === '';
}
